import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { track } from "@/lib/api";
import useSeo from "@/lib/useSeo";

const VALUES = [
  { title: "Restoring tired homes", text: "We take on properties that need real work and bring them back to a good, liveable standard rather than a quick cosmetic flip." },
  { title: "Improving local housing", text: "Every home we improve adds to the quality of housing in the Nottinghamshire communities we live and work in." },
  { title: "Building sustainably", text: "We're building a considered, long-term property business — not chasing quick wins at anyone's expense." },
  { title: "Working with local agents", text: "We aim to be a reliable, professional buyer that local estate agents can bring difficult properties to with confidence." },
  { title: "Long-term relationships", text: "Whether you're a vendor, an agent or an adviser, we'd rather build a relationship than do a one-off deal." },
  { title: "Properties others struggle with", text: "We're comfortable with probate, vacant, dated and difficult-to-sell homes that conventional buyers often walk away from." },
];

export default function About() {
  useSeo({ title: "About | Emerald Crown Property Investments Nottinghamshire", description: "The story of Emerald Crown Property Investments — local Nottinghamshire investors restoring tired homes and building a sustainable, relationship-led property business.", path: "/about" });
  useEffect(() => { track("page_view", { page: "about" }); }, []);

  return (
    <div className="pt-20">
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24">
        <span className="eyebrow">About us</span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-serif font-semibold text-slate-900 leading-tight">A genuine, local approach to property</h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          Emerald Crown Property Investments is a Nottinghamshire property business built on a simple idea: that tired,
          dated and overlooked homes deserve to be brought back to life properly.
        </p>
        <p className="mt-4 text-slate-600 leading-relaxed">
          We buy, renovate and invest in residential property across Mansfield, Kirkby-in-Ashfield, Hucknall, Nottingham
          and the surrounding areas. We're local — we know these streets, these communities and the kinds of homes that
          people want to live in. When we take on a property, our aim is to improve it thoughtfully and create somewhere
          genuinely good to live.
        </p>
        <p className="mt-4 text-slate-600 leading-relaxed">
          We're not a faceless "we buy any house" operation. We prefer to have honest conversations, be clear about what
          we can and can't do, and treat vendors, agents and advisers with respect. Some properties suit us and some
          don't — and we'll always tell you straight.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 80}>
              <div className="bg-white rounded-2xl border border-slate-200/80 p-8 h-full">
                <h3 className="text-xl font-serif text-emerald-950">{v.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-3xl overflow-hidden relative">
          <img src="https://images.unsplash.com/photo-1649083048770-82e8ffd80431?auto=format&fit=crop&q=85&w=1400" alt="Well-presented living space" className="w-full h-72 md:h-96 object-cover" />
          <div className="absolute inset-0 bg-emerald-950/70 flex items-center">
            <div className="px-8 md:px-16 max-w-xl">
              <h2 className="text-3xl font-serif text-white">Let's talk about your property</h2>
              <p className="mt-3 text-emerald-50/80">No pressure and no obligation — just a straightforward conversation.</p>
              <div className="mt-6 flex gap-3">
                <Link to="/sell"><Button className="bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-6">Sell your property</Button></Link>
                <Link to="/contact"><Button variant="outline" className="rounded-full border-white/40 text-white bg-white/5 hover:bg-white/15">Contact us</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
